import React, { useContext, useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import Typography from 'kingpin/atoms/Typography';

import FlexCentered from 'components/Common/FlexCentered';
import MetricCacheContext from 'contexts/MetricCacheContext';
import useContentMetricIds, { ContentInput } from 'hooks/useContentMetricIds';
import useGateLoadingFallback from 'hooks/useGateLoadingFallback';

import FleetOpsGrey from '../../images/fleetops-gray.svg';

type MetricsGateProps = {
  children: React.ReactNode;
} & Partial<ContentInput>;

const MetricsGate = ({ children, ...contentInput }: MetricsGateProps) => {
  const metricIds = useContentMetricIds(
    (contentInput as ContentInput).contentType
      ? (contentInput as ContentInput)
      : ({
          contentType: 'Scorecard',
          content: { kpis: [] },
        } as unknown as ContentInput),
  );
  const { requestMetricsByIds, isIdResolved, isIdErrored } =
    useContext(MetricCacheContext);

  useEffect(() => {
    if (metricIds.length > 0) {
      requestMetricsByIds(metricIds);
    }
  }, [metricIds, requestMetricsByIds]);

  const hasContentInput = (contentInput as ContentInput).contentType;
  const allResolved =
    metricIds.length === 0 || metricIds.every((id) => isIdResolved(id));
  const isResolving = !!hasContentInput && !allResolved;
  const { isLoading, fallback } = useGateLoadingFallback({
    contentInput,
    isResolving,
  });

  if (!hasContentInput) {
    return <>{children}</>;
  }

  if (metricIds.length === 0) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{fallback}</>;
  }

  const hasErrors = metricIds.some((id) => isIdErrored(id));

  const isCanvasBased =
    contentInput.contentType === 'Report' ||
    contentInput.contentType === 'Dashboard';
  if (hasErrors && !isCanvasBased) {
    return (
      <FlexCentered style={{ height: '100%' }}>
        <ReactSVG
          src={FleetOpsGrey}
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'center',
          }}
          beforeInjection={(svg) => {
            svg.setAttribute('style', 'width: 63px');
            svg.setAttribute('style', 'height: 72px');
          }}
        />
        <Typography.Body type="Body 14">Unable to load metrics</Typography.Body>
      </FlexCentered>
    );
  }

  return <>{children}</>;
};

export default MetricsGate;
